// src/pages/UserAddress.jsx

import React, { useRef, useState } from 'react';
import {
  FaPlus,
  FaHome,
  FaBriefcase,
  FaMapMarkerAlt,
  FaEllipsisV,
  FaEdit,
  FaTrash,
} from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import { useTheme } from '../../context/ThemeContext';
import '../../assets/css/UserAddress.css';

const initialForm = {
  full_name: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  country: 'India',
  postal_code: '',
  landmark: '',
  address_type: 'home',
};

// ================= FORM COMPONENT =================
// IMPORTANT: defined OUTSIDE UserAddress so React doesn't
// recreate it on every parent re-render — prevents input focus loss

const AddressForm = ({ formData, onChange, onSubmit, onCancel, saving, isNew }) => (
  <div className="ua-form-card" style={{marginBottom:"6px"}}>
    <form onSubmit={onSubmit}>
      <div className="row g-2">

        <div className="col-6">
          <input
            type="text"
            name="full_name"
            placeholder="Full name"
            className="form-control"
            value={formData.full_name}
            onChange={onChange}
            required
          />
        </div>

        <div className="col-6">
          <input
            type="text"
            name="phone"
            placeholder="Phone number"
            className="form-control"
            value={formData.phone}
            onChange={onChange}
            required
          />
        </div>

        <div className="col-6">
          <input
            type="text"
            name="postal_code"
            placeholder="Pincode"
            className="form-control"
            value={formData.postal_code}
            onChange={onChange}
          />
        </div>

        <div className="col-6">
          <input
            type="text"
            name="city"
            placeholder="City"
            className="form-control"
            value={formData.city}
            onChange={onChange}
          />
        </div>

        <div className="col-12">
          <textarea
            rows="2"
            name="address_line1"
            placeholder="Address line"
            className="form-control"
            value={formData.address_line1}
            onChange={onChange}
          />
        </div>

        <div className="col-6">
          <input
            type="text"
            name="state"
            placeholder="State"
            className="form-control"
            value={formData.state}
            onChange={onChange}
          />
        </div>

        <div className="col-6">
          <input
            type="text"
            name="landmark"
            placeholder="Landmark"
            className="form-control"
            value={formData.landmark}
            onChange={onChange}
          />
        </div>

        <div className="col-12">
          <select
            name="address_type"
            className="form-select"
            value={formData.address_type}
            onChange={onChange}
          >
            <option value="home">Home</option>
            <option value="office">Office</option>
          </select>
        </div>

      </div>

      <div className="ua-form-btns">
        <button type="submit" className="ua-save-btn" disabled={saving}>
          {saving ? 'Saving...' : isNew ? 'Save' : 'Update'}
        </button>
        <button type="button" className="ua-cancel-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>

    </form>
  </div>
);

// ================= MAIN COMPONENT =================

const UserAddress = () => {
  const { isDarkMode } = useTheme();
  const { addresses, addAddress, updateAddress, deleteAddress } = useUser();

  // null = no form | 'new' = add form at top | address.id = inline edit
  const [formOpenId, setFormOpenId] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  // ================= INPUT =================

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ================= ADD =================

  const handleAddNew = () => {
    setFormData(initialForm);
    setFormOpenId('new');
    setOpenMenuId(null);
  };

  // ================= EDIT =================

  const handleEdit = (address) => {
    setFormData({
      full_name: address.full_name || '',
      phone: address.phone || '',
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      country: address.country || 'India',
      postal_code: address.postal_code || '',
      landmark: address.landmark || '',
      address_type: address.address_type || 'home',
    });
    setFormOpenId(address.id);
    setOpenMenuId(null);
  };

  // ================= SAVE =================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (formOpenId === 'new') {
        await addAddress(formData);
      } else {
        await updateAddress(formOpenId, formData);
      }
      setFormOpenId(null);
    } finally {
      setSaving(false);
    }
  };

  // ================= DELETE =================

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm('Delete this address?');
    if (!confirmDelete) return;
    setOpenMenuId(null);
    await deleteAddress(id);
  };

  // ================= RENDER =================

  return (
    <div className={`user-address-wrapper ${isDarkMode ? 'address-dark' : ''}`}>
      <div className="container py-3">

        {/* HEADER */}
        <div className="ua-top-header">
          <h2>Manage addresses</h2>
          <button className="ua-add-btn" onClick={handleAddNew}>
            <FaPlus />
            Add address
          </button>
        </div>

        {/* ADD NEW FORM */}
        {formOpenId === 'new' && (
          <AddressForm
            formData={formData}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onCancel={() => setFormOpenId(null)}
            saving={saving}
            isNew
          />
        )}

        {/* LIST */}
        {addresses.length === 0 && formOpenId !== 'new' ? (
          <div className="ua-empty">
            <FaMapMarkerAlt />
            <p>No address found</p>
          </div>
        ) : (
          <div className="ua-address-list">
            {addresses.map((address) => (
              <div className="ua-address-card" key={address.id}>

                {/* CARD TOP */}
                <div className="ua-card-top">
                  <div className="ua-badge">
                    {address.address_type === 'home' ? (
                      <><FaHome /> HOME</>
                    ) : (
                      <><FaBriefcase /> OFFICE</>
                    )}
                  </div>

                  {/* THREE-DOT MENU */}
                  <div
                    className="ua-menu-wrap"
                    ref={openMenuId === address.id ? menuRef : null}
                  >
                    <button
                      className="ua-dot-btn"
                      onClick={() =>
                        setOpenMenuId(openMenuId === address.id ? null : address.id)
                      }
                      aria-label="Options"
                    >
                      <FaEllipsisV />
                    </button>
                    {openMenuId === address.id && (
                      <div className="ua-dropdown">
                        <button
                          className="ua-dd-item"
                          onClick={() => handleEdit(address)}
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          className="ua-dd-item ua-dd-delete"
                          onClick={() => handleDelete(address.id)}
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* NAME & PHONE */}
                <div className="ua-name-phone">
                  <h4>{address.full_name}</h4>
                  <span>{address.phone}</span>
                </div>

                {/* ADDRESS TEXT */}
                <p className="ua-address-text">
                  {address.address_line1}
                  {address.address_line2 && `, ${address.address_line2}`}
                  {address.landmark && `, ${address.landmark}`}
                  {`, ${address.city}, ${address.state}`}
                  {' — '}
                  <strong>{address.postal_code}</strong>
                </p>

                {/* INLINE EDIT FORM */}
                {formOpenId === address.id && (
                  <AddressForm
                    formData={formData}
                    onChange={handleChange}
                    onSubmit={handleSubmit}
                    onCancel={() => setFormOpenId(null)}
                    saving={saving}
                    isNew={false}
                  />
                )}

              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default UserAddress;